import { createEnumParam, useQueryParam, withDefault } from "use-query-params";
import React, {
  CSSProperties,
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Separator } from "@src/lib/ui/Separator";
import Text from "@src/lib/ui/Text";
import Spacer from "@src/lib/ui/Spacer";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import { cn } from "@src/lib/utils/cn";
import ChevronLeftIcon from "@src/lib/icons/12px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import {
  SubheaderTabButton,
  SubheaderTabList,
} from "@src/lib/ui/SubheaderTabStrip";

export interface QueryTabsProps<K extends string> {
  tabs: K[];
  dividerAfterTab?: K;
  hiddenTabs?: K[];
  render: QueryTabRenderFns<K>;
  labels: QueryTabCopy<K>;
  disabledFns?: QueryTabDisabledFns<K>;
  counts?: QueryTabCounts<K>;
  style?: CSSProperties;
  tabsContainerStyle?: CSSProperties;
  queryParamKey?: string;
  defaultTab?: K;
  contentContainerStyle?: CSSProperties;
  removeVerticalPadding?: Partial<Record<K, boolean>>;
  /**
   * When set, the tab strip is portaled here (e.g. `#top-nav-subheader`) so it
   * sits in the shell; tab content stays in place below. When unset the strip
   * renders in place, in a border-bottom row that matches the shell subheader.
   */
  subheaderPortal?: HTMLElement | null;
}

export function QueryTabs<K extends string>({
  tabs,
  dividerAfterTab,
  hiddenTabs,
  labels,
  disabledFns,
  render,
  style,
  tabsContainerStyle,
  queryParamKey = "tab",
  defaultTab,
  contentContainerStyle,
  counts,
  removeVerticalPadding,
  subheaderPortal,
}: QueryTabsProps<K>): ReactElement {
  const visibleTabs = useMemo(
    () =>
      !hiddenTabs ? tabs : tabs.filter((tab) => !hiddenTabs.includes(tab)),
    [tabs, hiddenTabs],
  );
  const [selectedTab, setSelectedTab] = useQueryParam(
    queryParamKey,
    withDefault(
      createEnumParam(visibleTabs),
      defaultTab ? defaultTab : visibleTabs[0],
    ),
  );

  const content = render[selectedTab];
  const shouldRemoveVerticalPadding =
    removeVerticalPadding?.[selectedTab] ?? false;

  const tabButtons = visibleTabs.map((tab) => {
    const isActive = selectedTab === tab;
    const isDisabled = disabledFns?.[tab]?.() ?? false;
    const count = counts?.[tab];
    return (
      <React.Fragment key={tab}>
        <SubheaderTabButton
          active={isActive}
          disabled={isDisabled}
          data-testid={`tab-${tab}`}
          onClick={() => setSelectedTab(tab, "replaceIn")}
          className={cn(
            "shrink-0",
            isDisabled && "cursor-not-allowed opacity-50",
          )}
        >
          {labels[tab]}
          {count != null && <QueryTabCount count={count} />}
        </SubheaderTabButton>
        {tab === dividerAfterTab && (
          <Separator
            orientation="vertical"
            className="mx-1 my-[9px] self-stretch bg-gray-5-alpha"
          />
        )}
      </React.Fragment>
    );
  });

  const tabStrip = subheaderPortal ? (
    <SubheaderTabsScroller style={tabsContainerStyle}>
      {tabButtons}
    </SubheaderTabsScroller>
  ) : (
    // Mirrors the shell subheader row (`#top-nav-subheader` in Page.tsx) so an
    // in-place strip is indistinguishable from a portaled one.
    <div className="flex min-h-10 w-full min-w-0 shrink-0 items-center border-b border-gray-2">
      <SubheaderTabList
        data-testid="subheader-tabs"
        className="hide-scrollbar flex min-h-0 min-w-0 flex-nowrap items-center gap-1 overflow-x-auto"
        style={tabsContainerStyle}
      >
        {tabButtons}
      </SubheaderTabList>
    </div>
  );

  return (
    <div
      className="flex flex-1 flex-col self-stretch"
      style={style}
      data-testid={"tabs"}
    >
      {subheaderPortal ? createPortal(tabStrip, subheaderPortal) : tabStrip}
      <div
        className="flex flex-1 self-stretch"
        style={{
          overflowX: "hidden",
          ...contentContainerStyle,
        }}
      >
        <ScrollableFlexGrow
          alignSelf={"stretch"}
          flex={1}
          style={{
            overflowY: "auto",
            zIndex: 0,
            position: "relative",
          }}
        >
          {shouldRemoveVerticalPadding ? null : <Spacer size={21} />}
          {typeof content === "function" ? content() : content}
          {shouldRemoveVerticalPadding ? null : <Spacer size={64} />}
        </ScrollableFlexGrow>
      </div>
    </div>
  );
}

/**
 * Wraps the subheader tab strip in a horizontally scrollable container and
 * fades the leading/trailing edges to signal that more tabs are reachable by
 * scrolling. The active underline is positioned inside the subheader row so it
 * remains visible even when the tab list scrolls horizontally.
 */
const SubheaderTabsScroller: FC<{
  children: React.ReactNode;
  style?: CSSProperties;
}> = ({ children, style }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  }, []);

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    event.preventDefault();
    el.scrollLeft += event.deltaY;
  }, []);

  const scrollByPage = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [children, updateScrollState]);

  return (
    <div className="relative order-first flex h-full min-w-0 w-0 flex-1 items-center overflow-hidden">
      <SubheaderTabList
        ref={scrollRef}
        data-testid="subheader-tabs"
        onWheel={handleWheel}
        className="hide-scrollbar flex h-full min-h-0 w-full min-w-0 flex-nowrap items-center gap-1 overflow-x-auto overflow-y-hidden"
        style={style}
      >
        {children}
      </SubheaderTabList>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent transition-opacity duration-150",
          canScrollLeft ? "opacity-100" : "opacity-0",
        )}
      />
      {canScrollLeft ? (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollByPage("left")}
          className="absolute left-0 flex size-6 items-center justify-center rounded-md bg-white/90 text-gray-8 shadow-[0_0_0_1px_rgba(10,10,10,0.06)] hover:bg-gray-1 hover:text-gray-12"
        >
          <ChevronLeftIcon />
        </button>
      ) : null}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent transition-opacity duration-150",
          canScrollRight ? "opacity-100" : "opacity-0",
        )}
      />
      {canScrollRight ? (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollByPage("right")}
          className="absolute right-0 flex size-6 items-center justify-center rounded-md bg-white/90 text-gray-8 shadow-[0_0_0_1px_rgba(10,10,10,0.06)] hover:bg-gray-1 hover:text-gray-12"
        >
          <ChevronRightIcon />
        </button>
      ) : null}
    </div>
  );
};

export type QueryTabRenderFns<K extends string> = Record<
  K,
  (() => React.ReactNode) | React.ReactNode
>;

export type QueryTabCopy<K extends string> = Record<K, string>;
export type QueryTabCounts<K extends string> = Partial<Record<K, number>>;

export type QueryTabDisabledFns<K extends string> = Record<K, () => boolean>;

export const QueryTabCount: FC<{
  count: number;
}> = (props) => {
  return (
    <div className="flex h-5 min-w-4 items-center justify-center rounded-md px-1 bg-gray-4-alpha">
      <Text variant="fine-print-title" className="text-gray-8">
        {props.count}
      </Text>
    </div>
  );
};
